import { useEffect, useRef, useState } from "react";
import type { OutputLine, DisplayItem } from "@/kernel/useKernel";
import { useKernel } from "@/kernel/useKernel";
import { GraphicIcon } from "@/components/Icon";

interface Props {
  output: OutputLine[];
  displays: DisplayItem[];
  running: boolean;
  inputRequest: boolean;
  onSubmitInput: (text: string) => void;
}

type Tab = "text" | "graphics";

const LINE_COLORS: Record<OutputLine["type"], string> = {
  stdout: "var(--text)",
  stderr: "var(--red)",
  error:  "var(--red)",
};

export default function Console({ output, displays, running, inputRequest, onSubmitInput }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevDisplays = useRef(0);

  const [tab, setTab]             = useState<Tab>("text");
  const [inputValue, setInputValue] = useState("");
  const [flash, setFlash]         = useState(false);
  // Taille du texte de la console, ajustable (lisibilité / densité).
  const [fontSize, setFontSize]   = useState(13);
  const bumpFont = (d: number) => setFontSize((s) => Math.max(10, Math.min(24, s + d)));

  // Quand la console est redimensionnée (poignée), on garde les DERNIÈRES lignes
  // visibles (comportement terminal type VS Code), au lieu de couper par le bas.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => { el.scrollTop = el.scrollHeight; });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Auto-scroll vers le bas à chaque nouvelle sortie (direct, pas smooth :
  // avec behavior:"smooth" les scrolls successifs rapides s'annulent).
  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [output, displays, inputRequest]);

  // Bascule automatiquement sur l'onglet Graphique quand un dessin arrive,
  // et déclenche une animation sur l'onglet pour attirer l'attention.
  useEffect(() => {
    if (displays.length > prevDisplays.current) {
      setTab("graphics");
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1500);
      prevDisplays.current = displays.length;
      return () => clearTimeout(t);
    }
    prevDisplays.current = displays.length;
  }, [displays.length]);

  // Une demande de saisie ramène sur l'onglet Texte et focus le champ
  useEffect(() => {
    if (inputRequest) {
      setTab("text");
      // focus après le rendu
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [inputRequest]);

  const submit = () => {
    onSubmitInput(inputValue);
    setInputValue("");
  };

  return (
    <div style={styles.wrapper}>
      <style>{CONSOLE_CSS}</style>
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(tab === "text" ? styles.tabActive : {}) }}
          onClick={() => setTab("text")}
          title="Sortie texte"
          aria-label="Sortie texte"
        >
          <span style={styles.replGlyph} aria-hidden="true">
            <span style={{ color: "var(--blue)" }}>&gt;</span>
            <span style={{ color: "var(--green)" }}>_</span>
          </span>
        </button>
        <button
          className={flash ? "console-tab-flash" : undefined}
          style={{ ...styles.tab, ...(tab === "graphics" ? styles.tabActive : {}) }}
          onClick={() => setTab("graphics")}
          title="Sortie graphique (turtle, matplotlib, etc.)"
          aria-label="Sortie graphique"
        >
          <GraphicIcon size={16} />
          {displays.length > 0 && <span style={styles.countBadge}>{displays.length}</span>}
        </button>

        {/* Taille du texte de la console */}
        <div style={styles.fontControls}>
          <button style={styles.fontBtn} onClick={() => bumpFont(-1)} disabled={fontSize <= 10} title="Réduire le texte" aria-label="Réduire le texte">A−</button>
          <button style={styles.fontBtn} onClick={() => bumpFont(1)} disabled={fontSize >= 24} title="Agrandir le texte" aria-label="Agrandir le texte">A+</button>
        </div>
      </div>

      <div ref={containerRef} style={{ ...styles.container, fontSize }}>
        {/* IMPORTANT : on rend TOUJOURS les deux branches dans le DOM et on
            cache la branche inactive via CSS. Sans ça, un switch d'onglet
            démonte le <canvas> → transferControlToOffscreen est définitif,
            le canvas ne peut PAS être ré-attaché proprement, l'input clavier
            est perdu et la boucle Python se retrouve à dessiner dans le vide.
            Avec ce pattern, CanvasBlock est monté UNE seule fois (à l'arrivée
            du 1er display canvas) et ne bouge plus jusqu'au prochain Exécuter. */}
        <div style={{ display: tab === "text" ? "contents" : "none" }}>
          {output.length === 0 && !running && !inputRequest && (
            <span style={styles.placeholder}>
              En attente d'exécution.
            </span>
          )}

          {output.map((line, i) => (
            <pre key={i} style={{ ...styles.line, color: LINE_COLORS[line.type] }}>
              {line.text}
            </pre>
          ))}

          {inputRequest && (
            <div style={styles.inputRow}>
              <span style={styles.prompt}>›</span>
              <input
                ref={inputRef}
                className="console-input"
                style={{ ...styles.input, fontSize }}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
                placeholder="Saisis ta réponse puis Entrée…"
                autoFocus
              />
            </div>
          )}

          {running && !inputRequest && (
            <span style={{ ...styles.line, color: "var(--blue)", fontStyle: "italic" }}>
              Exécution en cours…
            </span>
          )}
        </div>

        <div style={{ display: tab === "graphics" ? "contents" : "none" }}>
          {displays.length === 0 && (
            <span style={styles.placeholder}>
              Les dessins (turtle…) apparaîtront ici.
            </span>
          )}
          {displays.map((d, i) => (
            <DisplayBlock key={i} item={d} />
          ))}
        </div>

        <div ref={bottomRef} />
      </div>
    </div>
  );
}

function DisplayBlock({ item }: { item: DisplayItem }) {
  if (item.kind === "canvas") {
    return <CanvasBlock item={item} />;
  }
  // kind === "media" (SVG turtle/gamelib, PNG, JPEG)
  if (item.mime === "image/svg+xml") {
    return (
      <div
        className="console-svg"
        style={styles.svgBlock}
        dangerouslySetInnerHTML={{ __html: item.data }}
      />
    );
  }
  if (item.mime === "image/png" || item.mime === "image/jpeg") {
    return (
      <div style={styles.displayBlock}>
        <img
          src={`data:${item.mime};base64,${item.data}`}
          alt="output"
          style={{ maxWidth: "100%" }}
        />
      </div>
    );
  }
  // Fallback texte (HTML riche, etc.)
  return (
    <div
      style={{ ...styles.displayBlock, color: "var(--text)" }}
      dangerouslySetInnerHTML={{ __html: item.data }}
    />
  );
}

/** Sprint 5.A — bloc canvas piloté par OffscreenCanvas dans le worker. Pas
 *  rendu en 5.A car aucune leçon n'émet de `display_canvas_request` (gamelib
 *  garde le chemin SVG pour `affiche()`). Code en place pour le Sprint 5.B.
 *
 *  Garde-fous critiques :
 *  - `transferControlToOffscreen()` ne peut être appelée qu'UNE FOIS par
 *    élément <canvas>. On marque le node via `dataset.plTransferred` pour
 *    résister au démontage/remontage de React 18 Strict Mode.
 *  - Gating sur `kernel.ready` : le worker doit avoir terminé son init
 *    (`{type:"ready"}`) avant qu'on transfère le contrôle ; sinon les
 *    bridges `_pl_gamelib_caps` ne seront pas définis et tout appel Python
 *    silencieusement no-op.
 *  - Cleanup : on envoie `detachCanvas` au démontage pour que le worker
 *    libère son ctx2d et accepte un nouveau canvas après remontage. */
function CanvasBlock({
  item,
}: {
  item: Extract<DisplayItem, { kind: "canvas" }>;
}) {
  const kernel = useKernel();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!kernel.ready) return;          // attend que le worker soit prêt
    if (canvas.dataset.plTransferred === "1") return; // idempotence Strict Mode
    canvas.dataset.plTransferred = "1";
    kernel.attachCanvas(canvas, item.cssWidth, item.cssHeight);
    return () => {
      // Strict Mode dev démonte/remonte → on libère côté worker pour qu'il
      // accepte un nouveau attachCanvas sur le canvas remonté.
      kernel.detachCanvas();
    };
    // canvasRef est stable, kernel ne change pas → re-run uniquement sur
    // changement de dimensions ou de ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kernel.ready, item.cssWidth, item.cssHeight]);

  // Sprint 6 — écoute clavier au niveau DOCUMENT (pas du canvas).
  // Justification senior dev : exiger le focus du <canvas> est une UX cassée
  // pour le pédagogique. L'élève alterne entre éditer son code (Monaco) et
  // tester son jeu — il ne pense JAMAIS à cliquer la zone de jeu pour donner
  // le focus. Avec un listener canvas seul, les flèches sont consommées par
  // Monaco, le hero ne bouge pas.
  //
  // Solution : on capte au niveau document, et on ignore les évènements
  // émis depuis un champ d'édition (Monaco, <input>, <textarea>, contenteditable).
  // Effet : on peut taper son code sans interférence ET dès qu'on quitte
  // l'éditeur (clic ailleurs, hors zone d'édition), les flèches contrôlent
  // le hero, naturellement.
  //
  // Les clics restent capturés sur le canvas (cohérence : un clic sur la
  // scène pour interagir avec le jeu uniquement).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const isEditingTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      // Champs natifs
      const tag = target.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (target.isContentEditable) return true;
      // Monaco editor : son textarea caché a le focus, son conteneur a .monaco-editor
      if (target.closest(".monaco-editor")) return true;
      return false;
    };

    const rectXY = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditingTarget(e.target)) return;  // ne consomme PAS pendant l'édition
      // preventDefault uniquement sur les touches « de jeu » → évite que Espace
      // scrolle la page ou que les flèches scrollent.
      if ([" ", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
      }
      kernel.sendInputEvent({ kind: "keydown", key: e.key });
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (isEditingTarget(e.target)) return;
      kernel.sendInputEvent({ kind: "keyup", key: e.key });
    };
    const onClick = (e: MouseEvent) => {
      const { x, y } = rectXY(e);
      kernel.sendInputEvent({ kind: "click", x, y, button: e.button });
    };
    // window.blur (alt-tab) → on libère toutes les touches pour éviter qu'elles
    // restent « collées » côté worker.
    const onWindowBlur = () => kernel.sendInputEvent({ kind: "blur" });

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup",   onKeyUp);
    canvas.addEventListener("click",     onClick);
    window.addEventListener("blur",      onWindowBlur);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup",   onKeyUp);
      canvas.removeEventListener("click",     onClick);
      window.removeEventListener("blur",      onWindowBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kernel.ready]);

  return (
    <div style={styles.svgBlock}>
      <canvas
        ref={canvasRef}
        style={{
          width: item.cssWidth, height: item.cssHeight,
          borderRadius: 8, background: "transparent",
        }}
      />
    </div>
  );
}

// Règles globales scopées : SVG turtle responsive + centré, animation d'onglet.
const CONSOLE_CSS = `
.console-svg { display: flex; justify-content: center; align-items: flex-start; }
.console-svg svg { max-width: 100%; height: auto; background: #fff; border-radius: var(--radius); }
@keyframes console-tab-pulse {
  0%   { transform: scale(1);    color: var(--text); }
  30%  { transform: scale(1.18); color: var(--green); }
  100% { transform: scale(1);    color: var(--text); }
}
.console-tab-flash { animation: console-tab-pulse 0.5s ease-in-out 3; }
/* Champ de saisie input() : pas d'anneau de focus décalé (il débordait sur le prompt
   au-dessus, le rendant illisible). Repère de focus via la bordure bleue à la place. */
.console-input:focus, .console-input:focus-visible { outline: none; border-color: var(--blue); }
`;

const styles: Record<string, React.CSSProperties> = {
  // Zone de résultat en lecture seule : fond dédié, distinct du blanc de l'éditeur.
  wrapper: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "var(--bg-console)",
    boxShadow: "inset 0 2px 6px rgba(0, 0, 0, 0.18)",
  },
  tabs: {
    display: "flex",
    gap: 4,
    padding: "4px 8px 0",
    background: "var(--bg-console)",
    borderBottom: "1px solid var(--border-soft)",
    flexShrink: 0,
  },
  tab: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    color: "var(--text-muted)",
    padding: "5px 12px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 28,
  },
  tabActive: {
    color: "var(--text)",
    borderBottom: "2px solid var(--blue)",
  },
  // Contrôles de taille de texte, alignés à droite de la barre d'onglets.
  fontControls: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, paddingRight: 4 },
  fontBtn: {
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text-muted)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    padding: "3px 7px",
  },
  // Glyphe « >_ » : écho de la marque (chevron bleu + curseur vert), en mono.
  replGlyph: {
    fontFamily: "var(--font-mono)",
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: "-1px",
  },
  // Pastille de comptage des dessins reçus (remplace l'ancien « (2) »).
  countBadge: {
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    borderRadius: 8,
    background: "var(--blue)",
    color: "var(--on-accent)",
    fontSize: 10,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  container: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 12px",
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    fontSize: 13,
  },
  placeholder: {
    color: "var(--text-faint)",
    fontStyle: "italic",
  },
  line: {
    margin: "1px 0",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    margin: "2px 0",
  },
  prompt: { color: "var(--green)", fontWeight: 700 },
  input: {
    flex: 1,
    background: "var(--bg-base)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    color: "var(--text)",
    padding: "3px 8px",
    fontFamily: "inherit",
    fontSize: 13,
  },
  displayBlock: {
    margin: "8px 0",
  },
  svgBlock: {
    margin: "12px 0",
    width: "100%",
  },
};
